import { store } from './store';
import { t } from './i18n/en.js';

type PageModule = {
  render: (container: HTMLElement, params?: string[]) => Promise<void> | void;
};

type RouteLoader = () => Promise<PageModule>;

type NavigateOptions = {
  replace?: boolean;
};

const routes: Record<string, RouteLoader> = {
  '/': () => import('./pages/home.js'),
  '/gallery': () => import('./pages/gallery.js'),
  '/capture': () => import('./pages/capture.js'),
  '/nft': () => import('./pages/nft.js'),
  '/mint': () => import('./pages/mint.js'),
  '/about': () => import('./pages/about.js'),
  '/deploy-v2-devnet': () => import('./pages/deploy-v2-devnet.js'),
  '/deploy-v2-errors': () => import('./pages/deploy-v2-errors.js'),
  '/my-collection': () => import('./pages/my-collection.js'),
  '/timemachine': () => import('./pages/timemachine.js'),
  '/editor': () => import('./pages/editor.js'),
  '/admin': () => import('./pages/admin.js'),
};

const legacyRouteRedirects: Record<string, string> = {
  '/canon': '/',
  '/phases': '/',
  '/timeline': '/',
  '/project': '/about',
  '/lore': '/about',
};

let appContainer: HTMLElement | null = null;

function normalizePath(input: string | null | undefined): string {
  const raw = String(input || '/').trim();
  const withoutOrigin = raw.replace(/^https?:\/\/[^/]+/i, '');
  const withoutHash = withoutOrigin.startsWith('#') ? withoutOrigin.slice(1) : withoutOrigin;
  const pathname = withoutHash.split('?')[0] || '/';
  const cleaned = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return cleaned === '' ? '/' : cleaned.replace(/\/+$/, '') || '/';
}

function readCurrentPath(): string {
  const legacyHashPath = window.location.hash.startsWith('#/') ? window.location.hash.slice(1) : '';
  if (legacyHashPath) {
    const migrated = normalizePath(legacyHashPath);
    window.history.replaceState(window.history.state, '', migrated + window.location.search);
    return migrated;
  }
  return normalizePath(window.location.pathname);
}

function dispatchRouteChange(path: string, params: string[]): void {
  window.dispatchEvent(new CustomEvent('pros:routechange', {
    detail: { path, params },
  }));
}

async function resolve(): Promise<void> {
  if (!appContainer) {
    console.warn(t('router.resolveWarn'));
    return;
  }

  const path = readCurrentPath();
  const segments = path.split('/').filter(Boolean);
  const routeKey = '/' + (segments[0] || '');
  const redirected = legacyRouteRedirects[routeKey];
  if (redirected) {
    navigate(redirected, { replace: true });
    return;
  }

  const params = segments.slice(1);
  const loader = routes[routeKey] ?? routes['/'];
  store.set('route', { path: routeKey, params, raw: path, fullPath: path });
  dispatchRouteChange(routeKey, params);
  appContainer.setAttribute('data-loading', 'true');

  try {
    const module = await loader();
    appContainer.dispatchEvent(new CustomEvent('routerUnmount'));
    appContainer.innerHTML = '';
    await module.render(appContainer, params);
  } catch (err) {
    console.error(t('router.loadErrorPrefix'), routeKey, err);
    appContainer.innerHTML = `
      <div class="error-page">
        <p>${t('router.notFoundTitle')} <code>${routeKey}</code></p>
        <a href="/">${t('router.backHome')}</a>
      </div>
    `;
  } finally {
    appContainer.removeAttribute('data-loading');
  }
}

function handleDocumentNavigation(event: MouseEvent): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) return;

  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if (anchor.hasAttribute('download') || anchor.getAttribute('target') === '_blank' || anchor.dataset.routerIgnore === 'true') return;

  const href = anchor.getAttribute('href') || '';
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

  const url = new URL(anchor.href, window.location.origin);
  if (url.origin !== window.location.origin) return;
  if (url.hash && !url.pathname) return;

  event.preventDefault();
  navigate(url.pathname);
}

export function initRouter(container: HTMLElement): void {
  appContainer = container;
  window.addEventListener('popstate', () => { void resolve(); });
  document.addEventListener('click', handleDocumentNavigation);
  void resolve();
}

export function navigate(path: string, options: NavigateOptions = {}): void {
  const nextPath = normalizePath(path);
  const currentPath = normalizePath(window.location.pathname);
  if (nextPath === currentPath && !options.replace) {
    void resolve();
    return;
  }

  if (options.replace) {
    window.history.replaceState(window.history.state, '', nextPath);
  } else {
    window.history.pushState(window.history.state, '', nextPath);
  }
  void resolve();
}

export async function preloadRoute(path: string): Promise<PageModule | null> {
  const routePath = normalizePath(path);
  const segments = routePath.split('/').filter(Boolean);
  const routeKey = '/' + (segments[0] || '');
  const loader = routes[routeKey];
  if (!loader) return null;
  try {
    return await loader();
  } catch {
    return null;
  }
}

export function currentRoute(): ReturnType<typeof store.get<'route'>> {
  return store.get('route');
}
